import { GamepadDevice } from './Gamepad';
import { KeyboardMouse } from './KeyboardMouse';
import { TouchControls } from './TouchControls';
import { shapeAxis } from './Settings';

export type DeviceType = 'keyboard' | 'gamepad' | 'touch';

/**
 * Abstract action map (§8). Gameplay reads steerAxis / jump / cameraNudge /
 * pause — never raw keys or touches. Tracks the last-used device so the HUD can
 * swap prompts and hide touch controls when a physical input is active.
 */
export class InputManager {
  steerAxis = 0;
  cameraNudge = { x: 0, y: 0 };
  lastDevice: DeviceType = 'keyboard';
  onDeviceChange?: (d: DeviceType) => void;

  private kb = new KeyboardMouse();
  private gp = new GamepadDevice();
  readonly touch: TouchControls;

  private jumpQueued = false;
  private pauseQueued = false;

  constructor(touchParent: HTMLElement) {
    this.touch = new TouchControls(touchParent);
  }

  /** Called each frame before gameplay reads actions. */
  update(): void {
    this.gp.poll();

    // Pick the active steering device by recency of activity.
    let device: DeviceType = this.lastDevice;
    let rawSteer = 0;
    if (this.touch.active) {
      device = 'touch';
      rawSteer = this.touch.steer;
    } else if (this.gp.active) {
      device = 'gamepad';
      rawSteer = this.gp.steer;
    } else if (this.kb.active) {
      device = 'keyboard';
      rawSteer = this.kb.steer;
    } else {
      rawSteer = 0;
    }
    this.steerAxis = shapeAxis(rawSteer);

    if (device !== this.lastDevice) {
      this.lastDevice = device;
      this.onDeviceChange?.(device);
    }

    // Camera nudge (mouse + right stick), then decay.
    this.cameraNudge.x = this.kb.camNudge.x + this.gp.camNudge.x;
    this.cameraNudge.y = this.kb.camNudge.y + this.gp.camNudge.y;
    this.kb.camNudge.x *= 0.0;
    this.kb.camNudge.y *= 0.0;
    this.gp.camNudge.x = 0;
    this.gp.camNudge.y = 0;

    // Aggregate edge events.
    if (this.kb.jumpEdge || this.gp.jumpEdge || this.touch.jumpEdge) this.jumpQueued = true;
    if (this.kb.pauseEdge || this.gp.pauseEdge || this.touch.pauseEdge) this.pauseQueued = true;
    this.kb.jumpEdge = this.gp.jumpEdge = this.touch.jumpEdge = false;
    this.kb.pauseEdge = this.gp.pauseEdge = this.touch.pauseEdge = false;
  }

  consumeJump(): boolean {
    if (this.jumpQueued) {
      this.jumpQueued = false;
      return true;
    }
    return false;
  }

  consumePause(): boolean {
    if (this.pauseQueued) {
      this.pauseQueued = false;
      return true;
    }
    return false;
  }
}
